import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandFormSchema } from "@/schemas/brand";
import { Brand, ContentObjectiveGoals } from "@/types/brand";
import { BrandsToneOptions } from "@/types/pocketbase-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import LanguageSelect from "@/components/common/LanguageSelect";
import CountrySelect from "@/components/common/CountrySelect";

type FormValues = z.infer<typeof BrandFormSchema>;

interface BrandInformationFormProps {
  data: Brand;
  onSubmit: (data: Partial<Brand>) => void;
  mode: "onboarding" | "edit";
}

const contentObjectiveOptions: {
  value: ContentObjectiveGoals;
  label: string;
}[] = [
  {
    value: ContentObjectiveGoals.BrandAwareness,
    label: "🌟 Build Brand Awareness",
  },
  {
    value: ContentObjectiveGoals.EducateAudience,
    label: "📚 Educate Your Audience",
  },
  { value: ContentObjectiveGoals.DriveSales, label: "💰 Drive Sales" },
  {
    value: ContentObjectiveGoals.InternalKnowledge,
    label: "🧠 Create Internal Knowledge Base",
  },
  { value: ContentObjectiveGoals.ImproveSEO, label: "🔍 Improve SEO Rankings" },
  { value: ContentObjectiveGoals.BuildCommunity, label: "👥 Build Community" },
];

const toneOptions: {
  value: BrandsToneOptions;
  label: string;
}[] = [
  { value: BrandsToneOptions.Friendly, label: "😊 Friendly & Approachable" },
  { value: BrandsToneOptions.Professional, label: "👔 Professional & Formal" },
  {
    value: BrandsToneOptions.Conversational,
    label: "💬 Casual & Conversational",
  },
  {
    value: BrandsToneOptions.Authoritative,
    label: "📚 Expert & Authoritative",
  },
];

// Custom radio option component to avoid layout issues
const ToneRadioOption = ({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) => {
  return (
    <div
      className={`border rounded-md p-2 hover:bg-accent cursor-pointer ${
        checked ? "bg-accent/50" : ""
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center space-x-2">
        <div className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-primary">
          {checked && (
            <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
          )}
        </div>
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
};

// Custom checkbox option component to avoid layout issues
const ObjectiveCheckboxOption = ({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) => {
  return (
    <div
      className={`border rounded-md p-2 hover:bg-accent cursor-pointer ${
        checked ? "bg-accent/50" : ""
      }`}
      onClick={onToggle}
    >
      <div className="flex items-start space-x-2">
        <div className="flex h-3.5 w-3.5 items-center justify-center rounded-sm border border-primary mt-0.5">
          {checked && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-2.5 w-2.5 text-primary"
            >
              <path d="M5 12l5 5 9-9" />
            </svg>
          )}
        </div>
        <span className="text-sm">{label}</span>
      </div>
    </div>
  );
};

export function BrandInformationForm({
  data,
  onSubmit,
  mode,
}: BrandInformationFormProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(data.logo);

  const form = useForm<FormValues>({
    resolver: zodResolver(BrandFormSchema),
    defaultValues: {
      name: data.name,
      logo: data.logo,
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      description: data.description,
      targetAudience: data.targetAudience,
      tone: data.tone,
      contentObjectives: { goals: data.contentObjectives?.goals || [] },
      uniqueValue: data.uniqueValue,
      additionalNotes: data.additionalNotes,
      url: data.url,
      language: data.language || "English",
      location: data.location || "",
    },
  });

  const handleFormSubmit = (values: FormValues) => {
    // Convert null to undefined for logo to match Brand type
    // Ensure tone is properly typed as BrandsToneOptions
    const brandData: Partial<Brand> = {
      ...values,
      logo: values.logo === null ? undefined : values.logo,
      tone: values.tone as BrandsToneOptions,
    };
    onSubmit(brandData);
  };

  // Handle logo URL change and update preview
  const handleLogoUrlChange = (logoUrl: string) => {
    setLogoPreview(logoUrl);
  };

  const renderTitle = () => {
    if (mode === "onboarding") {
      return (
        <>
          <CardTitle>Brand Information</CardTitle>
          <CardDescription>
            Confirm or update your brand details to help us create tailored
            content
          </CardDescription>
        </>
      );
    }
    return null; // In edit mode, the title is handled by the parent component
  };

  const renderButtons = () => {
    return (
      <div className="flex justify-end pt-4">
        <Button type="submit" className="bg-black hover:bg-black/90">
          {mode === "onboarding" ? "Continue" : "Save"}
        </Button>
      </div>
    );
  };

  const formContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6 max-w-full"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Brand Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your Brand Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://yourbrand.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Brand Logo URL</FormLabel>
                <FormControl>
                  <div className="flex flex-col space-y-2">
                    <Input
                      placeholder="https://example.com/logo.png"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handleLogoUrlChange(e.target.value);
                      }}
                    />
                    {logoPreview && (
                      <div className="mt-2 border rounded-md p-2 w-24 h-24 flex items-center justify-center">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="max-w-full max-h-full object-contain"
                          onError={() => setLogoPreview(null)}
                        />
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="primaryColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary Color</FormLabel>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="color"
                        className="w-12 h-10 p-1"
                        {...field}
                      />
                      <Input
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                        className="flex-1"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="secondaryColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Secondary Color</FormLabel>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="color"
                        className="w-12 h-10 p-1"
                        {...field}
                      />
                      <Input
                        type="text"
                        value={field.value}
                        onChange={field.onChange}
                        className="flex-1"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Brand Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe your brand, products, and services"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="targetAudience"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Target Audience</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe your ideal customers or audience"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="language"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <LanguageSelect
                    value={field.value}
                    onChange={field.onChange}
                    description="Content will be created in this language"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CountrySelect
                    value={field.value || ""}
                    onChange={field.onChange}
                    description="Primary country where your brand operates"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="tone"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>Brand Tone</FormLabel>
              <FormControl>
                <div className="w-full">
                  <div className="grid grid-cols-2 gap-2">
                    {toneOptions.map((option) => (
                      <ToneRadioOption
                        key={option.value}
                        label={option.label}
                        checked={field.value === option.value}
                        onSelect={() => field.onChange(option.value)}
                      />
                    ))}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contentObjectives.goals"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <div className="space-y-1">
                <FormLabel>Content Objectives</FormLabel>
                <FormDescription>
                  Select all that apply to your content strategy
                </FormDescription>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {contentObjectiveOptions.map((option) => {
                  // Ensure we have an array, even if field.value is undefined
                  const currentGoals = field.value || [];
                  const isChecked =
                    Array.isArray(currentGoals) &&
                    currentGoals.includes(option.value);

                  return (
                    <ObjectiveCheckboxOption
                      key={option.value}
                      label={option.label}
                      checked={isChecked}
                      onToggle={() => {
                        // Toggle the checkbox state
                        const updatedGoals = isChecked
                          ? currentGoals.filter(
                              (value: ContentObjectiveGoals) =>
                                value !== option.value
                            )
                          : [...currentGoals, option.value];

                        // Update the form field
                        field.onChange(updatedGoals);
                      }}
                    />
                  );
                })}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="uniqueValue"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Unique Value Proposition</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="What makes your brand unique? What problem do you solve?"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="additionalNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Context (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Any other information that might help us understand your brand better"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {renderButtons()}
      </form>
    </Form>
  );

  // In onboarding mode, wrap in Card, in edit mode, return just the form
  if (mode === "onboarding") {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardHeader>{renderTitle()}</CardHeader>
        <CardContent>{formContent}</CardContent>
      </Card>
    );
  }

  return formContent;
}
